<template>
  <div v-if="me.data.value?.staffomaticUserId">
    <div class="flex items-center">
      <Information>
        <template #trigger>
          <button
            type="button"
            class="overflow-visible rounded-lg p-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-obsidian-600"
            :aria-label="t('timeTrackingStatus')"
            :disabled="isClockingOut || isClockingIn || isLoading"
            @click="showTimeTrackingModal = true"
          >
            <Spinner
              v-if="isClockingOut || isClockingIn || isLoading"
              class="size-5 text-obsidian-400"
            />
            <TimerIcon
              v-else
              class="size-5"
              :class="{
                'animate-issue-jump text-red-500 motion-reduce:animate-none': hasIssue,
                'text-black': !hasIssue,
              }"
            />
          </button>
        </template>
        <template #modal>
          <div v-if="myActiveEntry" class="flex items-center gap-1.5 text-sm">
            <TimerIcon class="size-4 shrink-0 text-slate-500" aria-hidden="true" />
            <span class="font-medium text-slate-500">{{ t('clockedInSinceLabel') }}</span>
            <span class="tabular-nums text-slate-800">{{ formattedLoggedInDuration }}</span>
          </div>
          <div v-else class="flex items-center gap-1.5 text-sm text-slate-600">
            <TimerIcon class="size-4 shrink-0 text-slate-400" aria-hidden="true" />
            <span>{{ t('timerNotRunning') }}</span>
          </div>
        </template>
      </Information>
    </div>
    <Modal v-model="showTimeTrackingModal" :content-class="'max-w-xl'" :show-close="true">
      <template #title>
        {{ t('timeTrackingStatus') }}
      </template>
      <div class="space-y-4">
        <div class="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm leading-snug">
          <div v-if="myActiveEntry" class="flex min-w-0 items-center gap-2">
            <TimerIcon class="size-4 shrink-0 text-slate-500" aria-hidden="true" />
            <span class="font-medium text-slate-500">{{ t('clockedInSinceLabel') }}</span>
            <span class="tabular-nums text-slate-800">{{ formattedLoggedInDuration }}</span>
          </div>
          <div v-else class="flex min-w-0 items-center gap-2 text-slate-600">
            <TimerIcon class="size-4 shrink-0 text-slate-400" aria-hidden="true" />
            <span>{{ t('timerNotRunning') }}</span>
          </div>
          <div v-if="requiresHubGeofencing" class="flex min-w-0 items-center gap-2">
            <MapPin class="size-4 shrink-0 text-slate-500" aria-hidden="true" />
            <span class="font-medium text-slate-500">{{ t('locationLabel') }}</span>
            <span v-if="isLocationLoading" class="inline-flex items-center gap-2 text-slate-800">
              <Spinner class="size-4 shrink-0 text-slate-400" />
              {{ t('locationLoading') }}
            </span>
            <span v-else-if="locationError" class="text-slate-800">{{
              t('locationUnavailable')
            }}</span>
            <span
              v-else-if="isValidatingLocation"
              class="inline-flex items-center gap-2 text-slate-800"
            >
              <Spinner class="size-4 shrink-0 text-slate-400" />
              {{ t('hubCheckLoading') }}
            </span>
            <span
              v-else-if="locationValidation?.valid"
              class="min-w-0 text-slate-800"
              :title="formattedCoordinates ?? undefined"
            >
              {{ nearestStationInfoText || formattedCoordinates }}
            </span>
            <span
              v-else-if="formattedCoordinates"
              class="min-w-0 text-slate-800"
              :title="formattedCoordinates"
            >
              {{ formattedCoordinates }}
            </span>
            <span v-else class="text-slate-400">—</span>
          </div>
        </div>
        <form class="space-y-4" @submit.prevent="onSubmit">
          <div v-if="requiresHubGeofencing && notAtHub && locationValidation" class="space-y-2">
            <h3 class="text-base font-semibold text-slate-900">{{ t('outsideHubTitle') }}</h3>
            <p class="text-sm leading-snug text-slate-700">
              {{
                myActiveEntry
                  ? t('outsideHubDescriptionClockOut')
                  : t('outsideHubDescriptionClockIn')
              }}
            </p>
          </div>
          <div v-if="clockedInTooLong" class="space-y-2 rounded-md bg-slate-50 p-3">
            <p class="text-sm font-medium text-slate-700">
              {{ t('clockedInTooLongTitle') }}
            </p>
            <SelectField
              name="clockOutMode"
              :label="t('clockOutModeLabel')"
              :options="clockOutModeOptions"
              :initial-value="'currentTime'"
            />
            <p class="text-xs text-slate-600">
              {{
                clockOutMode === 'maxDuration'
                  ? t(
                      requiresCommentFields ? 'useMaxDurationWithReasonHint' : 'useMaxDurationHint',
                      {
                        maxHours: MAX_SHIFT_DURATION_HOURS.toLocaleString(locale),
                      },
                    )
                  : t('useCurrentTimeHint')
              }}
            </p>
          </div>

          <div v-if="requiresCommentFields" class="space-y-3">
            <p v-if="!(notAtHub && locationValidation)" class="text-sm text-slate-600">
              {{ t('commentRequiredHint') }}
            </p>
            <InputField
              name="bookingNo"
              :label="t('bookingNo')"
              :placeholder="t('bookingNoPlaceholder')"
            />
            <TextareaField
              name="reason"
              :label="t('reason')"
              :placeholder="t('reasonPlaceholder')"
              :rows="3"
            />
          </div>
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div class="flex flex-wrap gap-2">
              <CVButton variant="secondary" type="button" @click="showTimeTrackingModal = false">
                {{ t('cancel') }}
              </CVButton>
              <CVButton
                v-if="requiresHubGeofencing"
                variant="secondary"
                type="button"
                :disabled="isLocationLoading"
                :is-loading="isLocationLoading"
                @click="refreshLocation"
              >
                {{ t('refreshLocation') }}
              </CVButton>
            </div>
            <CVButton
              variant="default"
              type="submit"
              :disabled="isClockingOut || isClockingIn"
              :is-loading="isClockingOut || isClockingIn"
            >
              {{ myActiveEntry ? t('clockOut') : t('clockIn') }}
            </CVButton>
          </div>
        </form>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { useMe } from '@/queries/use-users';
import {
  useClockInMyActiveTimeTrackingEntry,
  useClockOutMyActiveTimeTrackingEntry,
  useMyActiveTimeTrackingEntry,
} from '@/queries/use-time-tracking';
import { DateTime } from 'luxon';
import { MapPin, TimerIcon } from 'lucide-vue-next';
import { createToast } from 'mosha-vue-toastify';
import { computed, ref, watch } from 'vue';
import { isAxiosError } from 'axios';
import { useI18n } from 'vue-i18n';
import { Alert } from '@/services/alert.service';
import CVButton from '../buttons/CVButton.vue';
import Modal from '../Modal.vue';
import Information from '../Information.vue';
import { useNow } from '@vueuse/core';
import Spinner from '@/components/icons/Spinner.vue';
import { toTypedSchema } from '@vee-validate/yup';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import InputField from '@/components/InputField.vue';
import TextareaField from '@/components/TextareaField.vue';
import SelectField from '@/components/SelectField.vue';
import { useCurrentLocation } from '@/hooks/use-current-location';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import { timeTrackingService } from '@/api/time-tracking.service';
import type { ValidateLocationResponseDto } from '@carvia/ros-client-types';
import { useAbility } from '@casl/vue';

const MAX_SHIFT_DURATION_HOURS = 8.5;

const { t, locale } = useI18n();
const { getLocalizedField } = useLocalizedFields();
const showTimeTrackingModal = ref(false);
const me = useMe();
const { can } = useAbility();

const requiresHubGeofencing = computed(() => !can('clockInOutOutOfHub', 'TimeTracking'));

const { data: myActiveEntry, isLoading } = useMyActiveTimeTrackingEntry(
  computed(() => Boolean(me.data.value?.staffomaticUserId)),
);

const {
  mutateAsync: clockInMyActiveEntry,
  isPending: isClockingIn,
  error: clockInError,
} = useClockInMyActiveTimeTrackingEntry();
const {
  mutateAsync: clockOutMyActiveEntry,
  isPending: isClockingOut,
  error: clockOutError,
} = useClockOutMyActiveTimeTrackingEntry();

const isBadRequest = computed(() => {
  const error = myActiveEntry.value ? clockOutError.value : clockInError.value;
  return isAxiosError(error) && error.response?.status === 400;
});

const {
  position: currentPosition,
  error: locationError,
  isLoading: isLocationLoading,
  getCurrentPosition,
} = useCurrentLocation();

const formattedCoordinates = computed(() => {
  const c = currentPosition.value?.coords;
  if (c == null) {
    return null;
  }
  return `${c.latitude.toFixed(5)}, ${c.longitude.toFixed(5)}`;
});

const locationValidation = ref<ValidateLocationResponseDto | null>(null);
const isValidatingLocation = ref(false);

const nearestStationInfoText = computed(() =>
  getLocalizedField(locationValidation.value?.nearestStation?.info),
);

async function runLocationValidation() {
  const c = currentPosition.value?.coords;
  if (c == null) {
    locationValidation.value = null;
    return;
  }
  isValidatingLocation.value = true;
  try {
    locationValidation.value = await timeTrackingService.validateLocation({
      latitude: c.latitude,
      longitude: c.longitude,
    });
  } catch {
    locationValidation.value = null;
  } finally {
    isValidatingLocation.value = false;
  }
}

watch([showTimeTrackingModal, requiresHubGeofencing], ([open, geofencing]) => {
  if (open && geofencing) {
    void getCurrentPosition().catch(() => {
      /* error surfaced via locationError */
    });
  }
  if (!open) {
    locationValidation.value = null;
  }
});

watch(
  [showTimeTrackingModal, currentPosition, isLocationLoading, requiresHubGeofencing],
  async ([open, _pos, loading, geofencing]) => {
    if (!open || !geofencing) {
      return;
    }
    if (loading) {
      locationValidation.value = null;
      return;
    }
    if (!currentPosition.value?.coords) {
      return;
    }
    await runLocationValidation();
  },
);

async function refreshLocation() {
  try {
    await getCurrentPosition();
  } catch {
    /* error surfaced via locationError */
  }
}

const now = useNow({
  interval: 60_000,
});

const loggedInDuration = computed(() =>
  myActiveEntry.value?.startDate
    ? DateTime.fromJSDate(now.value).diff(DateTime.fromISO(myActiveEntry.value?.startDate))
    : undefined,
);

const formattedLoggedInDuration = computed(() => {
  if (!loggedInDuration.value || loggedInDuration.value.as('minutes') < 0) {
    return '00:00';
  }
  return loggedInDuration.value.toFormat('hh:mm');
});

const clockedInTooLong = computed(() => {
  if (!myActiveEntry.value) {
    return false;
  }
  return (loggedInDuration.value?.as('hours') ?? 0) > MAX_SHIFT_DURATION_HOURS;
});

const hasIssue = computed(() => !myActiveEntry.value || clockedInTooLong.value);

const notAtHub = computed(() => {
  if (!requiresHubGeofencing.value) {
    return false;
  }
  if (locationError.value) {
    return true;
  }
  if (locationValidation.value == null) {
    return false;
  }
  return !locationValidation.value.valid;
});

type ClockOutMode = 'maxDuration' | 'currentTime';

const clockOutModeOptions = computed(() => [
  { label: t('clockOutModeCurrentTime'), value: 'currentTime' },
  { label: t('clockOutModeMaxDuration'), value: 'maxDuration' },
]);

const validationSchema = computed(() =>
  toTypedSchema(
    yup.object({
      clockOutMode: yup.mixed<ClockOutMode>().oneOf(['currentTime', 'maxDuration']).required(),
      bookingNo: yup
        .string()
        .trim()
        .when('clockOutMode', {
          is: (mode: ClockOutMode) =>
            isBadRequest.value ||
            notAtHub.value ||
            (clockedInTooLong.value && mode !== 'maxDuration'),
          then: (schema) => schema.required().min(5),
          otherwise: (schema) => schema.optional(),
        }),
      reason: yup
        .string()
        .trim()
        .when('clockOutMode', {
          is: (mode: ClockOutMode) =>
            isBadRequest.value ||
            notAtHub.value ||
            (clockedInTooLong.value && mode !== 'maxDuration'),
          then: (schema) => schema.required().min(10),
          otherwise: (schema) => schema.optional(),
        }),
    }),
  ),
);

const { handleSubmit, resetForm, values } = useForm({
  validationSchema,
  initialValues: {
    bookingNo: '',
    reason: '',
    clockOutMode: 'currentTime',
  },
});

const clockOutMode = computed<ClockOutMode>(
  () => (values.clockOutMode as ClockOutMode) ?? 'currentTime',
);

const requiresCommentFields = computed<boolean>(() => {
  if (isBadRequest.value) {
    return true;
  }
  if (notAtHub.value) {
    return true;
  }
  if (clockedInTooLong.value) {
    return clockOutMode.value !== 'maxDuration';
  }
  return false;
});

const onSubmit = handleSubmit(async (values) => {
  const coords = currentPosition.value?.coords;
  if (requiresHubGeofencing.value && coords == null) {
    createToast(t('locationUnavailable'), {
      type: 'danger',
      position: 'bottom-right',
    });
    return;
  }

  let comment =
    requiresCommentFields.value && values.bookingNo?.trim() && values.reason?.trim()
      ? `Action: ${myActiveEntry.value ? 'Clock out' : 'Clock in'}\nBookingNo: ${values.bookingNo}\nReason: ${values.reason}`
      : undefined;

  if (comment && requiresHubGeofencing.value && coords) {
    comment = `Location: ${formattedCoordinates.value}\n` + comment;
  }

  const payload =
    requiresHubGeofencing.value && coords
      ? {
          latitude: coords.latitude,
          longitude: coords.longitude,
          comment,
        }
      : { comment };

  if (myActiveEntry.value) {
    if (isClockingOut.value) {
      return;
    }

    const result = await clockOutMyActiveEntry(
      clockedInTooLong.value && clockOutMode.value === 'maxDuration'
        ? { ...payload, useMaxDuration: true }
        : payload,
    );
    if (!result.clockedOut) {
      createToast(t('clockOutError'), {
        type: 'danger',
        position: 'bottom-right',
      });
      return;
    }
    showTimeTrackingModal.value = false;
    resetForm();
    await Alert.fire({
      icon: 'success',
      text: t('clockOutSuccessMessage'),
      confirmButtonText: t('clockOutSuccessThanks'),
      showCancelButton: false,
    });
    return;
  } else {
    if (isClockingIn.value) {
      return;
    }

    const result = await clockInMyActiveEntry(payload);
    if (!result.clockedIn) {
      createToast(t('clockInError'), {
        type: 'danger',
        position: 'bottom-right',
      });
      return;
    }
  }

  showTimeTrackingModal.value = false;
  resetForm();
});
</script>

<style scoped>
@keyframes issue-jump {
  0%,
  11%,
  100% {
    transform: translateY(0);
  }
  2.5% {
    transform: translateY(-3px);
  }
  4.5% {
    transform: translateY(0);
  }
  7% {
    transform: translateY(-2.5px);
  }
  9% {
    transform: translateY(0);
  }
}

.animate-issue-jump {
  animation: issue-jump 5s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .animate-issue-jump {
    animation: none;
  }
}
</style>

<i18n lang="json">
{
  "en": {
    "cancel": "Cancel",
    "bookingNo": "Booking number",
    "bookingNoPlaceholder": "e.g. 123456",
    "clockedInTooLongTitle": "You have been clocked in for a long time",
    "clockOutModeCurrentTime": "Use current time + reason",
    "clockOutModeLabel": "Clock-out time",
    "clockOutModeMaxDuration": "Use maximum duration",
    "clockIn": "Clock in",
    "clockInError": "Could not clock in",
    "clockOut": "Clock out",
    "clockOutError": "Could not clock out",
    "clockOutSuccessMessage": "Enjoy your day!",
    "clockOutSuccessThanks": "Thanks!",
    "clockedIn": "Clocked in",
    "commentRequiredHint": "Please add a booking number and a reason.",
    "reason": "Reason",
    "reasonPlaceholder": "What happened?",
    "useCurrentTimeHint": "Clock out using the current time and provide a reason.",
    "useMaxDurationWithReasonHint": "Clock out using the maximum shift duration ({maxHours} h).",
    "useMaxDurationHint": "Clock out using the maximum shift duration ({maxHours} h, no booking/reason required).",
    "timerNotRunning": "Not clocked in!",
    "timeTrackingStatus": "Time tracking",
    "clockedInSinceLabel": "Clocked in since",
    "refreshLocation": "Refresh location",
    "locationLabel": "Location",
    "locationLoading": "Locating…",
    "locationUnavailable": "Location unavailable",
    "hubCheckLoading": "Checking distance to your station…",
    "outsideHubTitle": "You are outside the hub",
    "outsideHubDescriptionClockOut": "You are not at your hub. Enter your booking number and the reason for your absence to clock out here. Alternatively, you can return to the hub and clock out there.",
    "outsideHubDescriptionClockIn": "You are not at your hub. Enter your booking number and the reason for your absence to clock in here. Alternatively, you can return to the hub and clock in there."
  },
  "de": {
    "cancel": "Abbrechen",
    "bookingNo": "Buchungsnummer",
    "bookingNoPlaceholder": "z.B. 123456",
    "clockedInTooLongTitle": "Hast du vergessen auszustempeln, oder bist du noch am arbeiten?",
    "clockOutModeCurrentTime": "Aktuelle Uhrzeit + Grund",
    "clockOutModeLabel": "Ausstempel-Zeit",
    "clockOutModeMaxDuration": "Maximale Dauer verwenden",
    "clockIn": "Einstempeln",
    "clockInError": "Einstempeln fehlgeschlagen",
    "clockOut": "Ausstempeln",
    "clockOutError": "Ausstempeln fehlgeschlagen",
    "clockOutSuccessMessage": "Schönen Feierabend!",
    "clockOutSuccessThanks": "Danke!",
    "clockedOut": "Ausgestempelt",
    "commentRequiredHint": "Bitte Buchungsnummer und Grund angeben.",
    "reason": "Grund",
    "reasonPlaceholder": "Was ist passiert?",
    "useCurrentTimeHint": "Mit aktueller Uhrzeit ausstempeln und einen Grund angeben.",
    "useMaxDurationWithReasonHint": "Mit maximaler Schichtdauer ({maxHours} Std) ausstempeln.",
    "useMaxDurationHint": "Mit maximaler Schichtdauer ({maxHours} Std) ausstempeln (ohne Buchung/Grund).",
    "timerNotRunning": "Nicht eingestempelt!",
    "timeTrackingStatus": "Zeiterfassung",
    "clockedInSinceLabel": "Eingestempelt seit",
    "refreshLocation": "Standort aktualisieren",
    "locationLabel": "Standort",
    "locationLoading": "Standort wird ermittelt…",
    "locationUnavailable": "Standort nicht verfügbar",
    "hubCheckLoading": "Abstand zur Station wird geprüft…",
    "outsideHubTitle": "Du befindest dich außerhalb des Hubs",
    "outsideHubDescriptionClockOut": "Du befindest dich nicht an deinem Hub. Gib deine Buchungsnummer sowie den Grund deiner Abwesenheit an, um hier auszustempeln. Alternativ kannst du zum Hub zurückkehren und dort ausstempeln.",
    "outsideHubDescriptionClockIn": "Du befindest dich nicht an deinem Hub. Gib deine Buchungsnummer sowie den Grund deiner Abwesenheit an, um hier einzustempeln. Alternativ kannst du zum Hub zurückkehren und dort einstempeln."
  },
  "es": {
    "cancel": "Cancelar",
    "bookingNo": "N.º de reserva",
    "bookingNoPlaceholder": "p. ej. 123456",
    "clockedInTooLongTitle": "Lleva mucho tiempo fichado",
    "clockOutModeCurrentTime": "Hora actual + motivo",
    "clockOutModeLabel": "Hora de salida",
    "clockOutModeMaxDuration": "Usar duración máxima",
    "clockIn": "Fichar entrada",
    "clockInError": "No se pudo fichar la entrada",
    "clockOut": "Fichar salida",
    "clockOutError": "No se pudo fichar la salida",
    "clockOutSuccessMessage": "¡Que tenga un buen día!",
    "clockOutSuccessThanks": "¡Gracias!",
    "clockedIn": "Entrada fichada",
    "commentRequiredHint": "Indique el n.º de reserva y un motivo.",
    "reason": "Motivo",
    "reasonPlaceholder": "¿Qué ha ocurrido?",
    "useCurrentTimeHint": "Fichar salida con la hora actual e indique un motivo.",
    "useMaxDurationWithReasonHint": "Fichar salida con la duración máxima del turno ({maxHours} h).",
    "useMaxDurationHint": "Fichar salida con la duración máxima del turno ({maxHours} h; no hace falta reserva ni motivo).",
    "timerNotRunning": "¡No hay entrada fichada!",
    "timeTrackingStatus": "Control horario",
    "clockedInSinceLabel": "Entrada desde",
    "refreshLocation": "Actualizar ubicación",
    "locationLabel": "Ubicación",
    "locationLoading": "Obteniendo ubicación…",
    "locationUnavailable": "Ubicación no disponible",
    "hubCheckLoading": "Comprobando la distancia a su estación…",
    "outsideHubTitle": "Está fuera del hub",
    "outsideHubDescriptionClockOut": "No está en su hub. Introduzca el n.º de reserva y el motivo de su ausencia para fichar la salida aquí. También puede volver al hub y fichar allí.",
    "outsideHubDescriptionClockIn": "No está en su hub. Introduzca el n.º de reserva y el motivo de su ausencia para fichar la entrada aquí. También puede volver al hub y fichar allí."
  }
}
</i18n>
